import AnimatedNumber from '../../../components/AnimatedNumber'
import { CaretDown, ChartBar, Crosshair, Check, Question } from '@phosphor-icons/react'
import { REPORT_HELP } from '../helpers/reportTextConstants'
import { useEffect, useId, useState } from 'react'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import Tooltip from './ReportTooltip'

function Help({ text }) {
  return (
    <Tooltip title={text} arrow>
      <span className="rp-help" tabIndex={0} aria-label={text}>
        <Question size={14} />
      </span>
    </Tooltip>
  )
}

function Info({ text }) {
  return <Help text={text} />
}

const METRIC_HELP = {
  planned: REPORT_HELP.planned,
  visited: REPORT_HELP.visited,
  unvisited: REPORT_HELP.unvisited,
  outside: REPORT_HELP.outside,
  attendedInRoute: 'Clientes en ruta con la actividad seleccionada registrada.',
  unattended: 'Clientes en ruta sin la actividad seleccionada registrada.',
  attendedOutside:
    'Clientes no programados en la ruta que tienen la actividad seleccionada registrada. No implica una visita válida por geocerca.',
  'geofence-visited':
    'Clientes únicos con estado visitado. Cada cliente cuenta una vez en el período.',
  'geofence-active':
    'Clientes únicos con la actividad seleccionada realizada dentro de geocerca. General incluye todas las actividades excepto reporte de visita y check-in/check-out.'
}
const SEGMENTS = [
  { key: 'general', label: 'General', activity: 'actividad', title: 'visita' },
  { key: 'order', label: 'Pedido', activity: 'pedido', title: 'pedidos' },
  { key: 'delivery', label: 'Entrega', activity: 'entrega', title: 'entregas' },
  { key: 'charge', label: 'Cobro', activity: 'cobro', title: 'cobros' }
]

const formatKpiPercentage = (value) => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? Math.round(numericValue) : value
}

function MetricButton({
  id,
  value,
  label,
  selected,
  onSelect,
  danger = false,
  disabled = false,
  helpKey = id
}) {
  const Metric = onSelect ? 'button' : 'div'
  return (
    <Metric
      type={onSelect ? 'button' : undefined}
      className={`rp-metric ${danger ? 'rp-metric-danger' : ''} ${!onSelect || disabled ? 'rp-metric--static' : ''}`}
      aria-label={`${label}: ${value}`}
      aria-pressed={onSelect ? selected === id : undefined}
      aria-disabled={disabled || undefined}
      disabled={onSelect ? disabled : undefined}
      onClick={() => {
        if (!disabled) onSelect?.(id)
      }}
    >
      <span className="rp-metric-top">
        <strong>{typeof value === 'number' ? <AnimatedNumber value={value} /> : value}</strong>
        {selected === id ? (
          <Check size={14} weight="bold" />
        ) : (
          <Info text={METRIC_HELP[helpKey] || label} />
        )}
      </span>
      <span>{label}</span>
    </Metric>
  )
}

export function RouteVisitEffectivenessCard({
  values = {},
  selected,
  onSelect,
  headerAction,
  selectableMetricIds,
  collapsible = false
}) {
  const [expanded, setExpanded] = useState(true)
  const contentId = useId()
  const isAttendance = values.variant === 'attendance'
  const title = values.title || 'Efectividad de visita'
  const heroTitle = values.heroTitle || 'Efectividad de visita'
  const formula =
    values.formula || 'Clientes visitados físicamente en ruta / Meta de clientes en el período'
  const info =
    values.info || 'Clientes visitados en ruta / clientes programados en el período × 100.'
  const labels = isAttendance
    ? {
        planned: 'Clientes en ruta (Meta)',
        visited: 'Atendidos en ruta',
        unvisited: 'Clientes sin atender',
        outside: 'Atendidos fuera de ruta'
      }
    : {
        planned: 'Clientes en ruta (Meta)',
        visited: 'Clientes visitados en ruta',
        unvisited: 'Clientes sin visitar',
        outside: 'Visitados fuera de ruta'
      }
  const metric = (id, value, label, danger = false, helpKey = id) => {
    const disabled = Array.isArray(selectableMetricIds) && !selectableMetricIds.includes(id)
    return (
      <MetricButton
        key={id}
        {...{ id, value, label, selected, onSelect, danger, disabled, helpKey }}
      />
    )
  }
  return (
    <article className="rp-kpi-frame">
      <div className="rp-kpi-card">
        <div className="rp-kpi-heading">
          <span className="rp-section-icon rp-orange">
            <ChartBar size={17} weight="fill" />
          </span>
          <h2>{title}</h2>
          {headerAction}
          {collapsible && (
            <button
              type="button"
              className="report-collapse-action"
              aria-label={title}
              aria-expanded={expanded}
              aria-controls={contentId}
              onClick={() => setExpanded(!expanded)}
            >
              <CaretDown size={16} className={expanded ? undefined : 'is-collapsed'} />
            </button>
          )}
        </div>
        <div
          id={contentId}
          hidden={collapsible && !expanded}
          className="report-kpi-body ubiqua-motion-enter"
        >
          <div className="rp-featured-metric rp-featured-metric--static">
            <strong className="rp-orange-text">
              <AnimatedNumber value={values.effectiveness} format={formatKpiPercentage} />
              {values.effectiveness != null && <span>%</span>}
            </strong>
            <span>
              <b>
                {heroTitle} <Info text={info} />
              </b>
              <small>{formula}</small>
            </span>
          </div>
          <div className="rp-mini-grid">
            {metric('planned', values.planned ?? '—', labels.planned)}
            {metric(
              isAttendance ? 'attendedInRoute' : 'visited',
              values.attendedInRoute ?? values.visited ?? '—',
              labels.visited
            )}
            {metric(
              isAttendance ? 'unattended' : 'unvisited',
              values.unattended ?? values.unvisited ?? '—',
              labels.unvisited
            )}
            {metric(
              'outside',
              values.outside ?? '—',
              labels.outside,
              true,
              isAttendance ? 'attendedOutside' : 'outside'
            )}
          </div>
          <p className="rp-kpi-footnote">
            {isAttendance ? 'Total atendidos' : 'Total visitados'}:{' '}
            <b>{values.totalAttended ?? values.totalVisited ?? '—'}</b>
            <Help
              text={
                isAttendance
                  ? 'Suma atendidos en ruta y atendidos fuera de ruta.'
                  : 'Incluye clientes visitados dentro y fuera de ruta.'
              }
            />
          </p>
        </div>
      </div>
    </article>
  )
}

export default function RouteExecutionKpis({
  selected,
  onSelect,
  values = {},
  isUpdating = false,
  segment: controlledSegment,
  onSegmentChange
}) {
  const [localSegment, setLocalSegment] = useState('general')
  const segment = controlledSegment ?? localSegment
  const setSegment = onSegmentChange ?? setLocalSegment
  const segments = SEGMENTS.filter(({ key }) => {
    if (key === 'general') return true

    // El endpoint puede devolver las categorías con sus contadores en cero.
    // No se ofrecen como pestaña hasta que exista una ejecución de esa
    // actividad dentro de la geocerca durante el período seleccionado.
    return (
      values.availableActivities?.includes(key) &&
      Number(values.geofence?.[key]?.active_clients) > 0
    )
  })
  const activeSegment = segments.find(({ key }) => key === segment) || SEGMENTS[0]
  useEffect(() => {
    if (activeSegment.key !== segment) setSegment(activeSegment.key)
  }, [activeSegment.key, segment, setSegment])
  const geofence = values.geofence?.[activeSegment.key]
  const conversion = geofence?.conversion
  const conversionHelp = `Clientes únicos con ${activeSegment.activity} en geocerca / clientes únicos visitados × 100. Sin clientes visitados, el porcentaje no se calcula.`
  const metric = (id, value, label, danger = false) => (
    <MetricButton key={id} {...{ id, value, label, selected, onSelect, danger }} />
  )
  return (
    <section
      className={`rp-kpis${isUpdating ? ' rp-kpis-updating' : ''}`}
      aria-label="Indicadores del reporte"
      aria-busy={isUpdating}
    >
      {isUpdating && (
        <span className="visually-hidden" role="status">
          Actualizando indicadores
        </span>
      )}
      <RouteVisitEffectivenessCard values={values} selected={selected} onSelect={onSelect} />
      <article className="rp-kpi-frame">
        <div className="rp-kpi-card">
          <div className="rp-kpi-heading">
            <span className="rp-section-icon">
              <Crosshair size={17} />
            </span>
            <h2>Geocerca</h2>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={activeSegment.key}
              aria-label="Tipo de conversión"
              onChange={(_, value) => value && setSegment(value)}
              className="rp-segment"
            >
              {segments.map(({ key, label }) => (
                <ToggleButton key={key} value={key}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
          <div className="rp-featured-metric rp-featured-metric--static">
            <strong>
              {conversion != null ? formatKpiPercentage(conversion) : '—'}
              {conversion != null && <span>%</span>}
            </strong>
            <span>
              <b>
                {`Conversión de ${activeSegment.title}`} <Info text={conversionHelp} />
              </b>
              <small>{`Con ${activeSegment.activity} en geocerca / Clientes únicos visitados`}</small>
            </span>
          </div>
          <div className="rp-mini-grid rp-mini-grid-two">
            {metric(
              'geofence-visited',
              geofence?.visited_clients ?? '—',
              'Clientes únicos visitados'
            )}
            {metric(
              'geofence-active',
              geofence?.active_clients ?? '—',
              `Clientes únicos con ${activeSegment.activity} en geocerca`
            )}
          </div>
          {conversion == null && (
            <p className="rp-kpi-footnote">
              {geofence
                ? 'Sin clientes únicos visitados para calcular la conversión.'
                : 'Datos de geocerca no disponibles.'}
            </p>
          )}
        </div>
      </article>
    </section>
  )
}
